import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Search, Menu, X, ChevronDown, User, Calculator, Star, Heart, Shield, Sparkles, Compass, Grid, Home as HomeIcon, FileText, LogOut, Package } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icon mapping
const categoryIcons = {
  'bracelets': '📿',
  'rudraksha': '🪷',
  'gemstones': '💎',
  'yantras': '✡️',
  'malas': '📿',
  'crystal': '🔮',
  'pyramids': '🔺',
  'murtis': '🛕',
  'pooja': '🪔',
  'paintings': '🖼️',
  'spray': '✨',
  'remedies': '🙏',
  'treatment': '💆',
  'pyrite': '🪨',
  '3d': '🎨',
  'candles': '🕯️',
  'oils': '🫒',
  'divs': '🪬',
  'springs': '💫',
  'negativity': '🛡️',
  'directional': '🧭',
  'devta': '🙏',
  'default': '🛍️',
};

const getCategoryIcon = (handle, name) => {
  const h = (handle || '').toLowerCase();
  const n = (name || '').toLowerCase();
  
  for (const [key, icon] of Object.entries(categoryIcons)) {
    if (h.includes(key) || n.includes(key)) {
      return icon;
    }
  }
  return categoryIcons.default;
};

const calculators = [
  { name: 'Daily Horoscope', href: '/horoscope', icon: Star, description: 'Get your daily predictions' },
  { name: 'Free Kundli', href: '/kundli', icon: Sparkles, description: 'Generate birth chart with PDF' },
  { name: 'Match Making', href: '/matchmaking', icon: Heart, description: 'Kundli Milan - 36 Gun' },
  { name: 'Dasha Predictions', href: '/dasha', icon: Calculator, description: 'Vimshottari Dasha timeline' },
  { name: 'Panchang', href: '/panchang', icon: Calculator, description: 'Daily auspicious timings' },
  { name: 'Numerology', href: '/numerology', icon: Calculator, description: 'Discover your numbers' },
  { name: 'Tarot Reading', href: '/tarot', icon: Sparkles, description: 'Divine card guidance' },
  { name: 'Love Calculator', href: '/love-calculator', icon: Heart, description: 'Check love compatibility' },
  { name: 'Dosha Analysis', href: '/dosha', icon: Shield, description: 'Manglik, Kalsarpa & more' },
  { name: 'Gem Recommendation', href: '/gem-recommendation', icon: Sparkles, description: 'Find your lucky stone' },
];

const vastuTools = [
  { name: 'Vastu Compass', href: '/vastu-compass', icon: Compass, description: '16 zones directional guide' },
  { name: 'Premium Vastu Tools', href: '/vastu-tools', icon: Grid, description: 'Pinnacle Vastu analysis' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const { cartCount, setIsCartOpen } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { customer: shopifyCustomer, isAuthenticated: isShopifyAuth, logout: shopifyLogout } = useCustomerAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`);
        const data = await response.json();
        // Filter out "Home page" and transform
        const filtered = data
          .filter(c => c.name.toLowerCase() !== 'home page' && c.handle !== 'frontpage')
          .map(c => ({
            name: c.name,
            href: `/products?category=${c.handle}`,
            icon: getCategoryIcon(c.handle, c.name),
          }));
        setCategories(filtered);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  return (
    <>
      {/* Main Navbar */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-md'
            : 'bg-white/80 backdrop-blur-md'
        }`}
        data-testid="navbar"
      >
        <nav className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="logo-link"
            >
              <img 
                src="/logo.png" 
                alt="Astro Vastu Remedies" 
                className="h-12 lg:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <Link
                to="/"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
                data-testid="nav-home"
              >
                Home
              </Link>
              
              {/* Shop Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-gold transition-colors">
                  Shop <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-amber-100 shadow-lg w-56 max-h-[70vh] overflow-y-auto">
                  <DropdownMenuLabel className="text-gold">All Categories ({categories.length})</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-amber-100" />
                  {categories.map((cat) => (
                    <DropdownMenuItem key={cat.name} asChild>
                      <Link to={cat.href} className="cursor-pointer hover:text-gold flex items-center gap-2">
                        <span>{cat.icon}</span> {cat.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className="bg-amber-100" />
                  <DropdownMenuItem asChild>
                    <Link to="/products" className="cursor-pointer text-gold font-medium">
                      View All Products →
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Calculators Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-gold transition-colors">
                  <Calculator className="w-4 h-4" /> Calculators <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-amber-100 shadow-lg w-56">
                  <DropdownMenuLabel className="text-gold">Free Calculators</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-amber-100" />
                  {calculators.map((calc) => (
                    <DropdownMenuItem key={calc.name} asChild>
                      <Link to={calc.href} className="cursor-pointer hover:text-gold">
                        <div className="flex items-start gap-3">
                          <calc.icon className="w-4 h-4 mt-0.5 text-gold" />
                          <div>
                            <p className="font-medium">{calc.name}</p>
                            <p className="text-xs text-muted-foreground">{calc.description}</p>
                          </div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Vastu Tools Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-gold transition-colors">
                  <Compass className="w-4 h-4" /> Vastu <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-amber-100 shadow-lg w-56">
                  <DropdownMenuLabel className="text-gold">Vastu Tools</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-amber-100" />
                  {vastuTools.map((tool) => (
                    <DropdownMenuItem key={tool.name} asChild>
                      <Link to={tool.href} className="cursor-pointer hover:text-gold">
                        <div className="flex items-start gap-3">
                          <tool.icon className="w-4 h-4 mt-0.5 text-gold" />
                          <div>
                            <p className="font-medium">{tool.name}</p>
                            <p className="text-xs text-muted-foreground">{tool.description}</p>
                          </div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/combos"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
              >
                Combos
              </Link>

              <Link
                to="/remedies"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
                data-testid="nav-remedies"
              >
                Remedies
              </Link>

              <Link
                to="/blog"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
                data-testid="nav-blog"
              >
                Blog
              </Link>

              <Link
                to="/about"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
                data-testid="nav-about"
              >
                About
              </Link>

              <Link
                to="/contact"
                className="text-sm font-medium text-foreground hover:text-gold transition-colors"
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Search */}
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground hover:text-gold hover:bg-gold/10"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                data-testid="search-btn"
              >
                <Search className="w-5 h-5" />
              </Button>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground hover:text-gold hover:bg-gold/10 relative"
                onClick={() => setIsCartOpen(true)}
                data-testid="cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full gold-gradient text-white text-xs font-bold flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>

              {/* Single Unified Account - Shopify Customer for Orders */}
              {isShopifyAuth ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-foreground hover:text-gold hover:bg-gold/10"
                      data-testid="account-btn"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center text-sm font-bold">
                        {shopifyCustomer?.name?.charAt(0).toUpperCase() || shopifyCustomer?.email?.charAt(0).toUpperCase() || 'A'}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white border-amber-100 shadow-lg w-56" align="end">
                    <DropdownMenuLabel className="text-foreground">
                      <p className="font-medium">{shopifyCustomer?.name || 'My Account'}</p>
                      <p className="text-xs text-muted-foreground font-normal">{shopifyCustomer?.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-amber-100" />
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="cursor-pointer hover:text-gold flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        My Orders
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="cursor-pointer hover:text-gold flex items-center gap-2">
                        <User className="w-4 h-4" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-amber-100" />
                    <DropdownMenuItem 
                      onClick={shopifyLogout}
                      className="cursor-pointer text-red-500 hover:text-red-600 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/account/login">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-foreground hover:text-gold hover:bg-gold/10"
                    data-testid="account-btn"
                    title="Sign in to track orders"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
              )}

              {/* Mobile Menu */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden text-foreground hover:text-gold hover:bg-gold/10"
                    data-testid="mobile-menu-btn"
                  >
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-white border-amber-100 w-80 overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="font-serif text-gold">Menu</SheetTitle>
                  </SheetHeader>
                  <nav className="mt-6 flex flex-col gap-2">
                    <Link
                      to="/"
                      className="text-base font-medium text-foreground hover:text-gold transition-colors py-2"
                    >
                      Home
                    </Link>
                    <Link
                      to="/products"
                      className="text-base font-medium text-foreground hover:text-gold transition-colors py-2"
                    >
                      All Products
                    </Link>
                    
                    {/* Categories */}
                    <div className="border-t border-amber-100 pt-4 mt-2">
                      <p className="text-sm font-semibold text-gold mb-3">Shop by Category</p>
                      <div className="grid grid-cols-2 gap-2">
                        {categories.map((cat) => (
                          <Link
                            key={cat.name}
                            to={cat.href}
                            className="flex items-center gap-2 text-sm text-foreground hover:text-gold transition-colors py-2 px-3 rounded-lg bg-cream hover:bg-gold/10"
                          >
                            <span>{cat.icon}</span> {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Calculators */}
                    <div className="border-t border-amber-100 pt-4 mt-2">
                      <p className="text-sm font-semibold text-gold mb-3 flex items-center gap-2">
                        <Calculator className="w-4 h-4" /> Free Calculators
                      </p>
                      <div className="space-y-1">
                        {calculators.map((calc) => (
                          <Link
                            key={calc.name}
                            to={calc.href}
                            className="flex items-center gap-3 text-sm text-foreground hover:text-gold transition-colors py-2 px-3 rounded-lg hover:bg-gold/10"
                          >
                            <calc.icon className="w-4 h-4 text-gold" />
                            <div>
                              <p className="font-medium">{calc.name}</p>
                              <p className="text-xs text-muted-foreground">{calc.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Vastu Tools */}
                    <div className="border-t border-amber-100 pt-4 mt-2">
                      <p className="text-sm font-semibold text-gold mb-3 flex items-center gap-2">
                        <Compass className="w-4 h-4" /> Vastu Tools
                      </p>
                      <div className="space-y-1">
                        {vastuTools.map((tool) => (
                          <Link
                            key={tool.name}
                            to={tool.href}
                            className="flex items-center gap-3 text-sm text-foreground hover:text-gold transition-colors py-2 px-3 rounded-lg hover:bg-gold/10"
                          >
                            <tool.icon className="w-4 h-4 text-gold" />
                            <div>
                              <p className="font-medium">{tool.name}</p>
                              <p className="text-xs text-muted-foreground">{tool.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Other Links */}
                    <div className="border-t border-amber-100 pt-4 mt-2">
                      <Link
                        to="/about"
                        className="block text-base font-medium text-foreground hover:text-gold transition-colors py-2"
                      >
                        About Us
                      </Link>
                      <Link
                        to="/contact"
                        className="block text-base font-medium text-foreground hover:text-gold transition-colors py-2"
                      >
                        Contact
                      </Link>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Search Bar */}
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden pb-4"
              >
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search for bracelets, rudraksha, gemstones..."
                    className="pl-12 bg-cream border-amber-200 focus:border-gold"
                    data-testid="search-input"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setIsSearchOpen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>
    </>
  );
};
